import { create } from 'zustand'
import { onboardingService } from '@/services/onboarding'

export type TourStep =
  | 'create-admin'
  | 'ask-financial'
  | 'show-financial'
  | 'show-students'
  | 'show-plans'
  | 'show-calendar'
  | 'completed'

interface TourState {
  isActive: boolean
  currentStep: TourStep
  useFinancial: boolean
  highlightTargets: string[]

  startTour: () => void
  nextStep: () => void
  closeTour: () => void
  setUseFinancial: (v: boolean) => void
  setHighlights: (targets: string[]) => void
}

const STEP_ORDER: TourStep[] = [
  'create-admin',
  'ask-financial',
  'show-financial',
  'show-students',
  'show-plans',
  'show-calendar',
  'completed',
]

function getNextStep(current: TourStep, useFinancial: boolean): TourStep {
  const idx = STEP_ORDER.indexOf(current)
  let next = STEP_ORDER[idx + 1] || 'completed'

  if (next === 'show-financial' && !useFinancial) {
    next = 'show-students'
  }

  return next
}

function getStoredStep(): TourStep | null {
  const stored = sessionStorage.getItem('dojopro:tour_step')

  return stored as TourStep | null
}

export const useTourStore = create<TourState>((set, get) => ({
  isActive:
    getStoredStep() !== null && getStoredStep() !== 'completed',
  currentStep: getStoredStep() || 'create-admin',
  useFinancial: false,
  highlightTargets: [],

  startTour: () => {
    sessionStorage.setItem('dojopro:tour_step', 'create-admin')
    set({ isActive: true, currentStep: 'create-admin' })
  },

  nextStep: () => {
    const { currentStep, useFinancial } = get()
    const next = getNextStep(currentStep, useFinancial)

    sessionStorage.setItem('dojopro:tour_step', next)

    if (next === 'completed') {
      set({ isActive: false, currentStep: 'completed', highlightTargets: [] })
      onboardingService.completeOnboarding().catch(() => {})
    } else {
      set({ currentStep: next, highlightTargets: [] })
    }
  },

  closeTour: () => {
    sessionStorage.setItem('dojopro:tour_step', 'completed')
    set({ isActive: false, currentStep: 'completed', highlightTargets: [] })
    onboardingService.completeOnboarding().catch(() => {})
  },

  setUseFinancial: (v) => set({ useFinancial: v }),
  setHighlights: (targets) => set({ highlightTargets: targets }),
}))
